import { marked } from 'marked'
import DOMPurify from 'dompurify'
import moment from 'moment'

// Same notice as Atlas's AI Insights.
export const DISCLAIMER_TEXT =
  'The insights presented here are generated by artificial intelligence and are intended to assist with analysis, not replace professional judgment. While we strive for accuracy, AI-generated content may contain errors, misinterpretations, or incomplete analysis. Please verify all data and conclusions independently before making business decisions.'

// Model output is untrusted: item descriptions in the prompt could steer it. Images are dropped
// too, since an image URL is the easiest way for injected text to send data elsewhere.
export function renderInsightsMarkdown(markdown) {
  if (!markdown) return ''
  const html = marked.parse(markdown, { gfm: true, async: false })
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['img', 'style', 'form', 'input', 'button', 'iframe'],
    FORBID_ATTR: ['style']
  })
}

const integer = value => (value === null || value === undefined ? 'n/a' : Number(value).toLocaleString('en-US'))

const percent = value => (value === null || value === undefined ? 'n/a' : `${Number(value).toFixed(1)}%`)

// Share of `whole`, matching the API's digest so the tiles and the report agree.
const share = (part, whole) => (whole ? `${((Number(part || 0) / Number(whole)) * 100).toFixed(1)}%` : null)

// Headline figures, computed by the API, shown above the report.
export function buildStatTiles(stats) {
  const overview = stats?.overview || {}
  const pricing = stats?.pricing || {}
  const isStore = stats?.level === 'store'
  const nonPerishable = (stats?.batch?.batch_type || '').toUpperCase() === 'NON PERISHABLE'

  const rowsDetail = [`${integer(overview.distinct_items)} items`]
  if (isStore) rowsDetail.push(`${integer(overview.stores)} stores`)

  const tiles = [
    { label: 'Rows', value: integer(overview.rows), detail: rowsDetail.join(' · ') },
    { label: 'Included', value: integer(overview.included), detail: `${integer(overview.excluded)} excluded` },
    {
      label: 'Avg discount',
      value: percent(pricing.discount_pct?.avg),
      detail: `median ${percent(pricing.discount_pct?.median)}`
    },
    {
      label: 'Avg net margin',
      value: percent(pricing.net_margin_pct?.avg),
      detail: `median ${percent(pricing.net_margin_pct?.median)}`
    },
    {
      label: 'Rows with rules',
      value: integer(overview.rows_with_rules),
      detail: share(overview.rows_with_rules, overview.rows) ? `${share(overview.rows_with_rules, overview.rows)} of rows` : ''
    },
    {
      label: 'SRP breaches',
      value: integer(overview.srp_breaches),
      detail: `of ${integer(overview.enforce_compliance)} compliance-enforced`
    }
  ]
  if (overview.pass_thru_checked) {
    tiles.push({
      label: 'Pass-through breaches',
      value: integer(overview.pass_thru_breaches),
      detail: `${share(overview.pass_thru_breaches, overview.pass_thru_checked)} of ${integer(overview.pass_thru_checked)} checked`
    })
  }
  if (nonPerishable) {
    tiles.push({
      label: 'Above base retail',
      value: integer(overview.above_base_retail),
      detail: share(overview.above_base_retail, overview.rows) ? `${share(overview.above_base_retail, overview.rows)} of rows` : ''
    })
  }
  return tiles
}

export function formatTimestamp(value) {
  if (!value) return ''
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm A') : ''
}

// Admin-only footer line: what the report cost and which models contributed.
export function formatUsage(result) {
  const usage = result?.usage
  if (!usage) return ''
  const parts = []
  parts.push(typeof usage.cost === 'number' ? `Cost $${usage.cost.toFixed(4)}` : 'Cost unavailable')
  parts.push(`${integer(usage.total_tokens)} tokens`)
  const models = result.models || []
  if (models.length > 1) parts.push(`${models.length} models${result.merged ? ', merged' : ''}`)
  else if (models.length === 1) parts.push(`1 model (${models[0]})`)
  return parts.join(' · ')
}

export function insightsErrorMessage(err) {
  const status = err?.response?.status
  const message = err?.response?.data?.message
  if (status === 403) return message || 'AI Insights are available for Price Pro stores only.'
  if (status === 404) return 'This batch could not be found. It may have been deleted.'
  if (status === 422) return 'The insights request was not valid for this batch.'
  if (status === 502) return message || 'AI Insights are temporarily unavailable. Please try again shortly.'
  if (status === 504 || err?.code === 'ECONNABORTED') return 'Generating insights took too long. Please try again.'
  if (message) return message
  if (!err?.response && err?.message === 'Network Error') return 'Could not reach the server. Please check your connection and try again.'
  return 'Failed to generate insights.'
}

export function slugify(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'batch'
}
